// Which sign-in options to show (WEB_PLAN.md §4.1), from GoTrue's public GET /auth/v1/settings with the anon key,
// like `SupabaseClient.authSettings()` / `AuthSettings(json:)` on iOS. Read once per page load (React Query), and
// again when a sign-in answer says an option was turned off meanwhile.
//
// The parsing and the button rules are pure functions (unit-tested); only `useAuthOptions` touches env and React.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { env } from '@/env';
import { NetworkError, ServerError } from './errors';

/** The project's sign-in switches (Swift `AuthSettings`). */
export interface AuthSettings {
  /** New accounts may be created (`!disable_signup`); existing ones sign in either way. */
  signupEnabled: boolean;
  email: boolean;
  phone: boolean;
  google: boolean;
  apple: boolean;
}

/**
 * From `{"disable_signup": bool, "external": {"email": bool, "phone": bool, "google": bool, "apple": bool, …}}`.
 * A missing switch reads as off, except email (GoTrue's default, and the one the console can't do without) and
 * sign-ups (only an explicit `disable_signup: true` turns them off). Throws on a body that isn't a JSON object.
 */
export function parseAuthSettings(json: unknown): AuthSettings {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) {
    throw new Error('Unexpected response from the server (auth settings).');
  }
  const obj = json as Record<string, unknown>;
  const rawExternal = obj.external;
  const external = typeof rawExternal === 'object' && rawExternal !== null && !Array.isArray(rawExternal)
    ? (rawExternal as Record<string, unknown>)
    : {};
  const flag = (key: string, fallback: boolean) => (typeof external[key] === 'boolean' ? (external[key] as boolean) : fallback);
  return {
    signupEnabled: !(obj.disable_signup === true),
    email: flag('email', true),
    phone: flag('phone', false),
    google: flag('google', false),
    apple: flag('apple', false),
  };
}

/** GET <supabaseUrl>/auth/v1/settings with the anon key (no session needed). */
export async function fetchAuthSettings(
  supabaseUrl: string,
  anonKey: string,
  init: { signal?: AbortSignal; fetchImpl?: typeof fetch } = {},
): Promise<AuthSettings> {
  const doFetch = init.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await doFetch(`${supabaseUrl.replace(/\/+$/, '')}/auth/v1/settings`, {
      method: 'GET',
      headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
      signal: init.signal,
    });
  } catch (e) {
    if ((e as { name?: string })?.name === 'AbortError') throw e;
    throw new NetworkError(e instanceof Error ? e.message : String(e));
  }
  const body: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const obj = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
    const code = typeof obj.error_code === 'string' ? obj.error_code : null;
    const message = [obj.msg, obj.message, obj.error_description].find((v) => typeof v === 'string') as string | undefined;
    throw new ServerError(response.status, code, message ?? `Server error (${response.status}).`);
  }
  return parseAuthSettings(body);
}

/** Build-time switches that decide whether this console can run a provider's popup at all. */
export interface ProviderConfig {
  googleWebClientId?: string;
  appleServicesId?: string;
  /** e2e: Google / Apple are stubs that mint the fake server's test tokens, so no ids are needed. */
  authStubs: boolean;
}

/** What the sign-in screens show. */
export interface AuthOptions {
  /** Email + password (and its Forgot password / Create account). */
  email: boolean;
  google: boolean;
  apple: boolean;
  /** "Create account" works; while off, the Create account screen explains it and points to Sign in. */
  signUp: boolean;
  /** The server's answer is known (before that: email only, sign-ups on, no provider buttons). */
  known: boolean;
}

/**
 * The buttons to show: email unless the server turned it off; Google / Apple only when the server has them on
 * AND this build can run them (a web client id / Services ID, or the e2e stubs). Phone isn't offered in the console
 * (Phase 1, WEB_PLAN.md §4.1). Unlike iOS, Google doesn't require Apple beside it: that is an App Store rule.
 * Until the answer arrives no provider button shows, so nobody clicks one the owner hasn't set up.
 */
export function authOptionsFrom(settings: AuthSettings | null | undefined, cfg: ProviderConfig): AuthOptions {
  if (!settings) return { email: true, google: false, apple: false, signUp: true, known: false };
  const google = settings.google && (cfg.authStubs || Boolean(cfg.googleWebClientId));
  const apple = settings.apple && (cfg.authStubs || Boolean(cfg.appleServicesId));
  // The email form also shows when nothing at all is on, so the card is never empty (SignInView.showsContactForm).
  const email = settings.email || (!google && !apple);
  return { email, google, apple, signUp: settings.signupEnabled, known: true };
}

/** "Apple or Google", "Apple", "Google", or null (AuthOptionsText.providers). */
export function providerNames(options: Pick<AuthOptions, 'apple' | 'google'>): string | null {
  const names: string[] = [];
  if (options.apple) names.push('Apple');
  if (options.google) names.push('Google');
  return names.length === 0 ? null : eitherList(names);
}

/** "A", "A or B", "A, B, or C" ("" for none) — AuthOptionsText.either. */
export function eitherList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
}

export const AUTH_SETTINGS_QUERY_KEY = ['auth', 'settings'] as const;

/** The sign-in options for this page load, plus `refresh()` after an "option turned off" answer. */
export function useAuthOptions(): AuthOptions & { loading: boolean; refresh: () => void } {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: AUTH_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => fetchAuthSettings(env.supabaseUrl, env.supabaseAnonKey, { signal }),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 2,
    refetchOnWindowFocus: false,
  });
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: AUTH_SETTINGS_QUERY_KEY });
  }, [queryClient]);
  const options = authOptionsFrom(query.data, {
    googleWebClientId: env.googleWebClientId,
    appleServicesId: env.appleServicesId,
    authStubs: env.authStubs,
  });
  return { ...options, loading: query.isPending, refresh };
}
